import OneSchemaImporter from '@oneschema/react';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { IBulkUploadTemplates, isTerminalStatus } from '../../../schemas/SourcingBulkUpload.schema';
import {
  SOURCING_LIST_COMPANIES_KEY,
  SOURCING_LIST_PEOPLE_KEY,
} from '../../../services/queries-hooks/SourcingListsQueriesHooks';
import {
  useBulkUploadStatusQuery,
  useBulkUploadTemplatesMutation,
} from '../../../services/queries-hooks/SourcingBulkUploadQueriesHooks';
import { SourcingList } from '../../../schemas/SourcingList.schema';
import { isDevelopment, isLocal } from '../../../util/environment';
import { trackEvent } from '../../../util/datadogTracking';
import { DIALOG_PX, dialogPaperSx } from '../sourcing-dialog.styles';

interface BulkUploadDialogProps {
  open: boolean;
  list: SourcingList;
  onClose: () => void;
  onLaunched?: () => void;
}

// OneSchema's webhook-mode success payload is typed as `any`. We expect each entry
// in `responses` to be our backend's `{ id, status }` JSON. Read it defensively.
function extractBulkUploadIdFromOneSchemaResponse(data: unknown): string | null {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const responses = (data as { responses?: unknown }).responses;
  if (!Array.isArray(responses)) {
    return null;
  }
  for (const entry of responses) {
    if (!entry || typeof entry !== 'object') {
      continue;
    }
    const body = (entry as { body?: unknown }).body;
    const parsed = typeof body === 'string' ? safeParseJson(body) : body;
    if (parsed && typeof parsed === 'object' && typeof (parsed as { id?: unknown }).id === 'string') {
      return (parsed as { id: string }).id;
    }
  }
  return null;
}

function safeParseJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

export const BulkUploadDialog: FC<BulkUploadDialogProps> = ({ open, list, onClose, onLaunched }) => {
  const { colors } = useTheme();
  const { pushSuccessToast, pushErrorToast } = useToastMessageState();
  const queryClient = useQueryClient();

  const [templates, setTemplates] = useState<IBulkUploadTemplates | null>(null);
  const [bulkUploadId, setBulkUploadId] = useState<string | null>(null);
  const [oneSchemaOpen, setOneSchemaOpen] = useState(false);

  const { mutate: requestTemplates } = useBulkUploadTemplatesMutation();
  const statusQuery = useBulkUploadStatusQuery(bulkUploadId ?? undefined);
  const status = statusQuery.data;

  const listQueryKey = useMemo(
    () =>
      list.entityType === 'company'
        ? [SOURCING_LIST_COMPANIES_KEY, list.id, 'company']
        : [SOURCING_LIST_PEOPLE_KEY, list.id, 'person'],
    [list.entityType, list.id]
  );

  const fetchedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      fetchedForRef.current = null;
      return;
    }
    const key = `${list.id}:${list.entityType}`;
    if (fetchedForRef.current === key) {
      return;
    }
    fetchedForRef.current = key;
    requestTemplates(
      { listId: list.id, entityType: list.entityType },
      {
        onSuccess: (data) => {
          setTemplates(data);
          setOneSchemaOpen(true);
        },
        onError: () => {
          onClose();
        },
      }
    );
  }, [open, list.id, list.entityType, requestTemplates, onClose]);

  useEffect(() => {
    if (open) {
      return;
    }
    setTemplates(null);
    setBulkUploadId(null);
    setOneSchemaOpen(false);
  }, [open]);

  const handledTerminalRef = useRef<string | null>(null);
  useEffect(() => {
    if (!status || handledTerminalRef.current === status.id) {
      return;
    }
    if (status.status === 'completed') {
      handledTerminalRef.current = status.id;
      const noun = list.entityType === 'company' ? 'companies' : 'people';
      pushSuccessToast({
        message: `Imported ${status.processedRecords} ${noun} into "${list.name}"`,
      });
      queryClient.invalidateQueries({ queryKey: listQueryKey });
      // Track last so a throwing SDK call can't block the toast / list refresh.
      trackEvent('sourcing.bulk_upload_completed', { success: true });
    } else if (status.status === 'failed') {
      handledTerminalRef.current = status.id;
      pushErrorToast({
        message: status.errorMessage ?? 'Bulk upload failed. Please try again.',
      });
      trackEvent('sourcing.bulk_upload_completed', { success: false });
    }
  }, [status, list.entityType, list.name, pushSuccessToast, pushErrorToast, queryClient, listQueryKey]);

  const handleOneSchemaSuccess = (data: unknown) => {
    setOneSchemaOpen(false);
    queryClient.invalidateQueries({ queryKey: listQueryKey });
    pushSuccessToast({ message: 'Bulk upload submitted. Items will appear shortly.' });
    const id = extractBulkUploadIdFromOneSchemaResponse(data);
    if (id) {
      setBulkUploadId(id);
      return;
    }
    onClose();
  };

  const handleOneSchemaError = () => {
    setOneSchemaOpen(false);
    pushErrorToast({ message: 'Bulk upload failed in OneSchema. Please try again.' });
    onClose();
  };

  const handleOneSchemaCancel = () => {
    setOneSchemaOpen(false);
    onClose();
  };

  const progressDialogOpen = open && bulkUploadId !== null;
  const isTerminal = status != null && isTerminalStatus(status.status);
  const progressPct = useMemo(() => {
    if (!status || status.totalRecords === 0) {
      return 0;
    }
    return Math.min(100, Math.round((status.processedRecords / status.totalRecords) * 100));
  }, [status]);

  return (
    <>
      {templates && oneSchemaOpen && (
        <OneSchemaImporter
          isOpen={oneSchemaOpen}
          clientId={templates.clientId}
          userJwt={templates.token}
          templateKey={templates.templateKey}
          importConfig={{ type: 'webhook', key: templates.webhookKey }}
          devMode={isDevelopment() || isLocal()}
          className='oneschema-importer'
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 2147483647,
          }}
          onSuccess={handleOneSchemaSuccess}
          onError={handleOneSchemaError}
          onCancel={handleOneSchemaCancel}
          onRequestClose={handleOneSchemaCancel}
          onLaunched={onLaunched}
        />
      )}

      <Dialog
        open={progressDialogOpen}
        onClose={isTerminal ? onClose : undefined}
        maxWidth={false}
        slotProps={{ paper: { sx: dialogPaperSx } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 16,
            letterSpacing: '0.15px',
            lineHeight: 1.5,
            color: alpha(colors.primary[100], 0.87),
            pt: '16px',
            pb: '10px',
            mb: '12px',
            px: DIALOG_PX,
          }}
        >
          {status?.status === 'completed'
            ? 'Bulk upload complete'
            : status?.status === 'failed'
              ? 'Bulk upload failed'
              : 'Bulk upload in progress'}
          {isTerminal && (
            <IconButton size='small' aria-label='Close' onClick={onClose}>
              <CloseIcon sx={{ fontSize: 20, color: colors.neutral[40] }} />
            </IconButton>
          )}
        </DialogTitle>

        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '16px', px: DIALOG_PX }}>
          {!status && (
            <Box>
              <LinearProgress />
              <Typography variant='body2' color='text.secondary' sx={{ mt: '8px' }}>
                Loading upload status…
              </Typography>
            </Box>
          )}

          {status && (
            <>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '4px' }}>
                  <Typography variant='body2' color='text.secondary'>
                    {status.processedRecords} of {status.totalRecords} processed
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {progressPct}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant={isTerminal ? 'determinate' : 'indeterminate'}
                  value={progressPct}
                  color={status.status === 'failed' ? 'error' : 'primary'}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: '24px' }}>
                <CountTile label='Imported' value={status.processedRecords} />
                <CountTile label='Skipped' value={status.skippedRecords} />
                <CountTile label='Failed' value={status.failedRecords} />
              </Box>

              {status.status === 'completed' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: colors.success[60] }}>
                  <CheckCircleIcon sx={{ fontSize: 20 }} />
                  <Typography variant='body2'>Import finished successfully.</Typography>
                </Box>
              )}

              {status.status === 'failed' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: colors.critical[50] }}>
                  <ErrorOutlineIcon sx={{ fontSize: 20 }} />
                  <Typography variant='body2'>
                    {status.errorMessage ?? 'The upload could not be completed.'}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        {isTerminal && (
          <DialogActions sx={{ justifyContent: 'flex-end', py: '16px', px: DIALOG_PX }}>
            <Button variant='contained' color='secondary' onClick={onClose}>
              Close
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

const CountTile: FC<{ label: string; value: number }> = ({ label, value }) => {
  const { colors } = useTheme();
  return (
    <Box>
      <Typography variant='caption' color='text.secondary'>
        {label}
      </Typography>
      <Typography variant='h6' sx={{ color: alpha(colors.primary[100], 0.87) }}>
        {value}
      </Typography>
    </Box>
  );
};
